import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart drawer behavior.
 *
 * Keeps the theme's native cart rendering and drawer lifecycle while adding
 * the competitor-style Note, Shipping and Coupon utility panels.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.62;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('click', this.#handleUtilityClick);
    this.addEventListener('change', this.#handleUtilityChange);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('click', this.#handleUtilityClick);
    this.removeEventListener('change', this.#handleUtilityChange);
  }

  /**
   * Updates sticky state and initializes the shipping country selector.
   */
  #handleDrawerOpen = () => {
    this.#closeUtilityPanels();
    this.#initializeShippingForms();
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Opens the cart drawer only after a successful add-to-cart operation.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add';

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#initializeShippingForms();
            this.#updateStickyState();
          });
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAfterSuccess = () => {
          requestAnimationFrame(() => {
            if (!this.#themeDrawer?.isOpen) {
              this.#themeDrawer?.open();
            }

            settle();
          });
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAfterSuccess, { once: true });
        } else {
          openAfterSuccess();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-drawer] Event promise rejected:', error);
        }
      });
  };

  /**
   * Handles Note, Shipping and Coupon controls by event delegation so the
   * controls continue working after Section Rendering API DOM updates.
   *
   * @param {MouseEvent} event
   */
  #handleUtilityClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-cart-addon-trigger]');
    if (trigger instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#toggleUtilityPanel(trigger.dataset.cartAddonTrigger || '');
      return;
    }

    const closeButton = target.closest('[data-cart-addon-close]');
    if (closeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#closeUtilityPanels();
      return;
    }

    const saveNoteButton = target.closest('[data-cart-note-save]');
    if (saveNoteButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#closeUtilityPanels();
      return;
    }

    const calculateButton = target.closest('[data-cart-shipping-calculate]');
    if (calculateButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#calculateShipping(calculateButton);
    }
  };

  /**
   * @param {Event} event
   */
  #handleUtilityChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    if (target.matches('[data-cart-shipping-country]')) {
      this.#populateProvinceSelector(target);
    }
  };

  /**
   * @param {string} panelName
   */
  #toggleUtilityPanel(panelName) {
    if (!panelName) return;

    const panel = this.querySelector(`[data-cart-addon-panel="${CSS.escape(panelName)}"]`);
    if (!(panel instanceof HTMLElement)) return;

    const wasOpen = !panel.hidden;
    this.#closeUtilityPanels();

    if (wasOpen) return;

    panel.hidden = false;
    panel.classList.add('is-active');

    const trigger = this.querySelector(`[data-cart-addon-trigger="${CSS.escape(panelName)}"]`);
    if (trigger instanceof HTMLButtonElement) {
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
    }

    if (panelName === 'shipping') {
      this.#initializeShippingForms();
    }

    requestAnimationFrame(() => {
      const focusable = panel.querySelector('textarea, input, select, button');
      if (focusable instanceof HTMLElement) {
        focusable.focus({ preventScroll: true });
      }
      this.#updateStickyState();
    });
  }

  #closeUtilityPanels() {
    this.querySelectorAll('[data-cart-addon-panel]').forEach((panel) => {
      if (panel instanceof HTMLElement) {
        panel.hidden = true;
        panel.classList.remove('is-active');
      }
    });

    this.querySelectorAll('[data-cart-addon-trigger]').forEach((trigger) => {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    });

    requestAnimationFrame(() => this.#updateStickyState());
  }

  #initializeShippingForms() {
    this.querySelectorAll('[data-cart-shipping-country]').forEach((countrySelect) => {
      if (!(countrySelect instanceof HTMLSelectElement)) return;

      if (!countrySelect.dataset.initialized) {
        const defaultCountry = countrySelect.dataset.defaultCountry;
        if (defaultCountry) {
          const matchingOption = Array.from(countrySelect.options).find(
            (option) => option.value === defaultCountry || option.textContent?.trim() === defaultCountry
          );
          if (matchingOption) countrySelect.value = matchingOption.value;
        }

        countrySelect.dataset.initialized = 'true';
      }

      this.#populateProvinceSelector(countrySelect);
    });
  }

  /**
   * @param {HTMLSelectElement} countrySelect
   */
  #populateProvinceSelector(countrySelect) {
    const form = countrySelect.closest('[data-cart-shipping-form]');
    if (!(form instanceof HTMLElement)) return;

    const provinceSelect = form.querySelector('[data-cart-shipping-province]');
    const provinceField = form.querySelector('[data-cart-shipping-province-field]');
    if (!(provinceSelect instanceof HTMLSelectElement) || !(provinceField instanceof HTMLElement)) return;

    const selectedOption = countrySelect.options[countrySelect.selectedIndex];
    const rawProvinces = selectedOption?.dataset.provinces || '[]';

    /** @type {Array<[string, string]>} */
    let provinces = [];

    try {
      provinces = JSON.parse(rawProvinces);
    } catch (error) {
      console.warn('[cart-drawer] Unable to parse province data:', error);
    }

    const previousValue = provinceSelect.value;
    provinceSelect.replaceChildren();

    provinces.forEach(([label, value]) => {
      const option = document.createElement('option');
      option.value = value || label;
      option.textContent = label;
      provinceSelect.appendChild(option);
    });

    const hasProvinces = provinces.length > 0;
    provinceField.hidden = !hasProvinces;
    provinceSelect.disabled = !hasProvinces;

    if (hasProvinces && Array.from(provinceSelect.options).some((option) => option.value === previousValue)) {
      provinceSelect.value = previousValue;
    }
  }

  /**
   * @param {HTMLButtonElement} calculateButton
   */
  async #calculateShipping(calculateButton) {
    const form = calculateButton.closest('[data-cart-shipping-form]');
    if (!(form instanceof HTMLElement)) return;

    const country = form.querySelector('[data-cart-shipping-country]');
    const province = form.querySelector('[data-cart-shipping-province]');
    const zip = form.querySelector('[data-cart-shipping-zip]');
    const results = form.querySelector('[data-cart-shipping-results]');

    if (
      !(country instanceof HTMLSelectElement) ||
      !(province instanceof HTMLSelectElement) ||
      !(zip instanceof HTMLInputElement) ||
      !(results instanceof HTMLElement)
    ) {
      return;
    }

    if (!zip.value.trim()) {
      zip.focus();
      results.hidden = false;
      results.innerHTML = '<p class="tc-cart-shipping__message" role="alert">Enter a ZIP or postal code.</p>';
      return;
    }

    calculateButton.disabled = true;
    calculateButton.setAttribute('aria-busy', 'true');
    results.hidden = false;
    results.innerHTML = '<p class="tc-cart-shipping__message">Calculating shipping rates…</p>';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const parameters = new URLSearchParams({
        'shipping_address[country]': country.value,
        'shipping_address[province]': province.disabled ? '' : province.value,
        'shipping_address[zip]': zip.value.trim(),
      });

      const response = await fetch(`${root}cart/shipping_rates.json?${parameters.toString()}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      const payload = await response.json();

      if (!response.ok) {
        const message = this.#extractShippingError(payload);
        throw new Error(message);
      }

      const rates = Array.isArray(payload.shipping_rates) ? payload.shipping_rates : [];

      if (!rates.length) {
        results.innerHTML = '<p class="tc-cart-shipping__message">No shipping rates were found for this address.</p>';
        return;
      }

      const list = document.createElement('ul');
      list.className = 'tc-cart-shipping__rates';

      rates.forEach((rate) => {
        const item = document.createElement('li');
        item.className = 'tc-cart-shipping__rate';

        const name = document.createElement('span');
        name.textContent = rate.name || rate.presentment_name || 'Shipping';

        const price = document.createElement('span');
        price.textContent = this.#formatShippingPrice(rate.price, rate.currency);

        item.append(name, price);
        list.appendChild(item);
      });

      results.replaceChildren(list);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to calculate shipping rates.';
      results.innerHTML = '';
      const paragraph = document.createElement('p');
      paragraph.className = 'tc-cart-shipping__message';
      paragraph.setAttribute('role', 'alert');
      paragraph.textContent = message;
      results.appendChild(paragraph);
    } finally {
      calculateButton.disabled = false;
      calculateButton.removeAttribute('aria-busy');
      requestAnimationFrame(() => this.#updateStickyState());
    }
  }

  /**
   * @param {unknown} payload
   */
  #extractShippingError(payload) {
    if (payload && typeof payload === 'object') {
      const errors = /** @type {{ zip?: string[] | string; base?: string[] | string }} */ (payload);
      const candidate = errors.zip || errors.base;

      if (Array.isArray(candidate)) return candidate.join(' ');
      if (typeof candidate === 'string') return candidate;
    }

    return 'Unable to calculate shipping rates for this address.';
  }

  /**
   * @param {string | number} amount
   * @param {string | undefined} currency
   */
  #formatShippingPrice(amount, currency) {
    const numericAmount = Number.parseFloat(String(amount || 0));
    const activeCurrency = currency || window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: activeCurrency,
      }).format(numericAmount);
    } catch (_error) {
      return `${numericAmount.toFixed(2)} ${activeCurrency}`;
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
